import React, { useEffect, useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard, Sparkles, Settings as SettingsIcon, ShieldAlert,
  Grid3x3, Activity as ActivityIcon,
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { cn } from "../lib/utils";

const ALL_ITEMS = [
  { to: "/",         label: "Dashboard",       icon: LayoutDashboard, end: true },
  { to: "/apps",     label: "Apps",            icon: Grid3x3 },
  { to: "/news",     label: "Industry Alerts", icon: ShieldAlert, testid: "nav-industry-alerts", showBadge: true },
  { to: "/ask-ai",   label: "Ask AI",          icon: Sparkles },
  { to: "/activity", label: "Activity",        icon: ActivityIcon },
  { to: "/settings", label: "Settings",        icon: SettingsIcon, superAdminOnly: true },
];

export default function TopNav() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "Super Admin";
  const items = ALL_ITEMS.filter((it) => !it.superAdminOnly || isSuperAdmin);
  const [alertCount, setAlertCount] = useState(0);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get("/industry-news", { params: { state: "new", limit: 1 } });
      setAlertCount(data?.counts?.new ?? data?.total_new ?? 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60_000);
    const onFocus = () => loadCount();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [loadCount]);

  return (
    <div className="w-full bg-background border-b border-border">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8">
        <nav className="flex items-center gap-1 h-12 overflow-x-auto tabler-scroll">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={it.testid}
              className={({ isActive }) => cn(
                "relative inline-flex items-center gap-2 h-12 px-3.5 text-[13.5px] font-medium transition-colors whitespace-nowrap",
                isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {({ isActive }) => (
                <>
                  <it.icon className={cn("w-4 h-4", isActive ? "text-primary" : "")} />
                  {it.label}
                  {it.showBadge && alertCount > 0 && (
                    <span
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10.5px] font-bold leading-none shadow-sm"
                      data-testid="nav-industry-alerts-badge"
                    >
                      {alertCount > 99 ? "99+" : alertCount}
                    </span>
                  )}
                  <span
                    className={cn(
                      "pointer-events-none absolute left-2 right-2 -bottom-px h-0.5 rounded-full",
                      isActive ? "bg-primary" : "bg-transparent"
                    )}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
